import React from 'react'

export type ChargeType = 'percentage' | 'flat'
export type ChargeName = 'vat' | 'tax'
export interface ChargeChange {
    type?: ChargeType
    percentage?: number
    amount?: number
}

/** VAT / TAX amount: a percentage of the base amount, or the flat amount entered. */
export const resolveChargeAmount = (baseAmount: number, type: ChargeType | undefined, percentage: number, amount: number) => {
    if (type === 'flat') {
        return Number(amount) || 0
    }
    return (baseAmount * (Number(percentage) || 0)) / 100
}

/** Applies a %/Flat, percentage or flat amount change to vatType/vatPercentage/vatAmount (or tax*). */
export const applyChargeChange = <T extends Record<string, any>>(purchase: T, charge: ChargeName, change: ChargeChange): T => ({
    ...purchase,
    ...(change.type !== undefined ? { [charge + 'Type']: change.type } : {}),
    ...(change.percentage !== undefined ? { [charge + 'Percentage']: change.percentage } : {}),
    ...(change.amount !== undefined ? { [charge + 'Amount']: change.amount } : {}),
})

/**
 * VAT / TAX a goods receive takes over from its PO. A percentage stays a percentage; a flat amount becomes
 * the received share of it (the whole flat amount when everything left on the PO is received).
 */
export const chargeFromPo = (po: Record<string, any>, charge: ChargeName, receiveAmount: number) => {
    if (po[charge + 'Type'] === 'flat') {
        const poAmount = Number(po.total_amount || 0) - Number(po.discount_amount || 0) + Number(po.carrying_cost || 0)
        const amount = poAmount > 0 ? Math.round((Number(po[charge + 'Amount'] || 0) * receiveAmount / poAmount) * 100) / 100 : 0
        return { type: 'flat' as ChargeType, percentage: 0, amount }
    }
    const percentage = Number(po[charge] || 0)
    return { type: 'percentage' as ChargeType, percentage, amount: (receiveAmount * percentage) / 100 }
}

interface ChargeInputProps {
    charge: ChargeName
    label: string
    type?: ChargeType
    percentage: number
    amount: number
    onChange: (charge: ChargeName, change: ChargeChange) => void
}

/** VAT / TAX row of a summary box: %/Flat, then the percentage and its amount, or the flat amount. */
const ChargeInput: React.FC<ChargeInputProps> = ({ charge, label, type, percentage, amount, onChange }) => {
    const chargeType: ChargeType = type === 'flat' ? 'flat' : 'percentage'
    return (
        <div className="row mt-2 align-items-center">
            <div className="col-md-5"><strong>{label}: </strong></div>
            <div className="col-md-7">
                <div className="input-group input-group-sm">
                    <select
                        name={charge + 'Type'}
                        value={chargeType}
                        onChange={(event) => onChange(charge, { type: event.target.value as ChargeType })}
                        className="form-select form-select-sm"
                        style={{ maxWidth: '75px' }}
                    >
                        <option value="percentage">%</option>
                        <option value="flat">Flat</option>
                    </select>
                    {chargeType === 'percentage' && (
                        <>
                            <input
                                type="number"
                                name={charge + 'Percentage'}
                                value={percentage || ''}
                                onChange={(event) => onChange(charge, { percentage: parseFloat(event.target.value) || 0 })}
                                className="form-control form-control-sm"
                                style={{ maxWidth: '80px' }}
                                min={0}
                                step={'any'}
                            />
                            <span className="input-group-text">%</span>
                        </>
                    )}
                    <input
                        type="number"
                        name={charge + 'Amount'}
                        id={charge + 'Amount'}
                        value={chargeType === 'flat' ? (amount || '') : (Math.round((Number(amount) || 0) * 100) / 100)}
                        onChange={(event) => onChange(charge, { amount: parseFloat(event.target.value) || 0 })}
                        className="form-control form-control-sm text-end"
                        placeholder={chargeType === 'flat' ? 'Amount' : undefined}
                        readOnly={chargeType === 'percentage'}
                        min={0}
                        step={'any'}
                    />
                </div>
            </div>
        </div>
    )
}

export default ChargeInput
